import React, { Component, PropTypes } from 'react';
import Keys from 'es6/core/keys';

/**
 * Passes keydown events to the keyInput.
 *
 * @this IME
 */
function handleKeydown(e) {
  // Prevent default key behavior if the IME is active.
  if (!this.props.disabled && e.keyCode === Keys.ENTER) {
    e.preventDefault();
  }

  if (!this.props.disabled && this.props.onKeyDown) {
    this.props.onKeyDown(e);
  }

  this.keyInput.keydown(e);
}

/**
 * Passes keypress events to the keyInput
 *
 * @this IME
 */
function handleKeypress(e) {
  this.keyInput.keypress(e);
}

/**
 * Handles responses from the key input.
 *
 * @this IME
 * @param {String} input
 * @param {Number} cursorIndex
 * @param {Number} letterCount
 */
function handleKeyInputKeyPress(input, cursorIndex, letterCount) {
  const { autoConfirm, language } = this.props;

  // Allow spelling practice to auto commit for English only. Other
  // languages will change input in place, so it is hard to detect
  // when it should be committed.
  if (autoConfirm && input.length === letterCount && language === 'en') {
    this.keyInput.confirm();
  }
}

/**
 * Pass this up if an event was specified.
 *
 * @this IME
 */
function handleTextWasConfirmed(...args) {
  const { onTextWasConfirmed } = this.props;

  if (onTextWasConfirmed) {
    onTextWasConfirmed(...args);
  }
}

/**
 * @this IME
 */
function setText() {
  const { language, text } = this.props;
  this.keyInput.setText({ text, language });
}

/**
 * Wrapper for the legacy IME.
 *
 * Smartfm.Apps.Behavior.KeyInput needs to be available on the global namespace.
 */
class IME extends Component {
  constructor(props) {
    super(props);

    this.handleKeydown = handleKeydown.bind(this);
    this.handleKeypress = handleKeypress.bind(this);

    this.keyInput = null;
  }

  componentDidMount() {
    const keypress = handleKeyInputKeyPress.bind(this);
    const textWasConfirmed = handleTextWasConfirmed.bind(this);

    // Set up the key input, after setting the state, the other properties will be initialized
    // in the componentDidUpdate() method.
    this.keyInput = new Smartfm.Apps.Behavior.KeyInput(
      this.refs.ime,
      { keypress, textWasConfirmed, correctLettersTypeable: this.props.correctLettersTypeable }
    );

    // For some reason, focusing the input element before *and* after (but not one or the other)
    // fixes this bug when using a native IME on Chrome: https://iknowjp.atlassian.net/browse/NUF-9
    // TODO: Figure out why this fixes it...
    this.refs.input.focus();
    setText.call(this);

    if (this.props.disabled) {
      this.keyInput.disable();
    } else {
      this.keyInput.enable();
    }

    // Add events to pass input to the key input.
    window.addEventListener('keypress', this.handleKeypress);
    window.addEventListener('keydown', this.handleKeydown);
  }

  /**
   * Only update when the text or disabled status have changed.
   */
  shouldComponentUpdate(nextProps) {
    const { disabled, text } = this.props;
    return nextProps.text !== text || nextProps.disabled !== disabled;
  }

  componentDidUpdate(prevProps) {
    const { disabled, text } = this.props;

    if (prevProps.text !== text) {
      setText.call(this);
    }

    if (prevProps.disabled !== disabled) {
      if (disabled) {
        this.keyInput.disable();
      } else {
        this.keyInput.enable();
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keypress', this.handleKeypress);
    window.removeEventListener('keydown', this.handleKeydown);
  }

  getActualHtml() {
    this.keyInput.actualHtml();
  }

  getCorrectHtml() {
    this.keyInput.correctRepresentationHtml();
  }

  rebuild() {
    this.keyInput.rebuild();
  }

  render() {
    return (
      <div ref="ime" style={{ display: 'inline-block' }}>
        <input ref="input" type="text" />
        <div className="text" />
      </div>
    );
  }
}

IME.propTypes = {
  autoConfirm: PropTypes.bool,
  disabled: PropTypes.bool,
  language: PropTypes.string.isRequired,
  onTextWasConfirmed: PropTypes.func,
  onKeyDown: PropTypes.func,
  text: PropTypes.string.isRequired,
  correctLettersTypeable: PropTypes.bool,
};

IME.defaultProps = {
  autoConfirm: false,
  disabled: false,
  correctLettersTypeable: false,
};

export default IME;

/**
 * Takes the HTML string returned by the legacy KeyInput's actualHtml()/correctRepresentationHtml()
 * methods, and returns an array of plain objects representing the visual state of the IME
 * represented in the HTML.
 *
 * The returned array contains objects with a 'type' property, which can be one of: IME_WORD,
 * IME_SPACE, IME_PUNCTUATION, IME_ITEM. IME_ITEM objects contain a 'letters' property, which is an
 * array of objects of the form:
 *
 * `{ isTypeable: boolean, isIncorrect: boolean, hasCursor: boolean, text: string }`.
 *
 * All other types contain a 'text' property containing the text of the word/punctuation/space.
 *
 * If the function encounters any unexpected markup or classnames in the given string, it wil throw
 * an error in development environments, and ignore the unexpected markup, dropping it from the
 * output, on production environments.
 */
export function parseIMEHtml(html) {
  // Instead of parsing the HTML string, just create an element and traverse its children using the
  // browser's DOM traversal methods.
  const container = document.createElement('div');
  container.innerHTML = html;

  return parseDOM(container);
}

export const IME_WORD        = 'word';
export const IME_SPACE       = 'space';
export const IME_PUNCTUATION = 'punctuation';
export const IME_ITEM        = 'item';

function parseDOM(node) {
  const result = [];

  for (let i = 0; i < node.childNodes.length; i++) {
    const child = node.childNodes[i];

    if (child.nodeName === 'SPAN') {
      if (child.classList.contains('space')) {
        result.push({ type: IME_SPACE, text: child.textContent });
      } else if (child.classList.contains('punctuation')) {
        result.push({ type: IME_PUNCTUATION, text: child.textContent });
      } else if (child.classList.contains('item')) {
        result.push(parseItem(child));
      } else {
        result.push({ type: IME_WORD, text: child.textContent });
      }
    } else {
      result.push({ type: IME_WORD, text: child.textContent });
    }
  }

  return result;
}

function parseItem(node) {
  const letters = [];

  for (let i = 0; i < node.childNodes.length; i++) {
    const child = node.childNodes[i];

    if (child.nodeName === 'SPAN' && child.classList.contains('letter') && !child.classList.contains('excluded')) {
      letters.push({
        isTypeable: child.classList.contains('typeable'),
        isIncorrect: child.classList.contains('incorrect'),
        hasCursor: child.classList.contains('cursor'),
        text: child.textContent,
      });
    } else {
      letters.push({
        isTypeable: false,
        isIncorrect: false,
        hasCursor: false,
        text: child.textContent,
      });
    }
  }

  return { type: IME_ITEM, letters };
}
